import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';
import { useSettings } from '@/context/SettingsContext';
import { 
  Building2, ArrowLeft, TrendingUp, Clock, AlertTriangle, Package,
  DollarSign, CheckCircle, XCircle, Calendar, FileText, Plus,
  CreditCard, Wallet, Receipt, ChevronUp, ChevronDown, Percent,
  Truck, BarChart3, PieChart, Check, Trash2, Download
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import axios from 'axios';
import { DateInput } from '@/components/ui/date-input';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const PAYMENT_MODES = [
  { value: "bank_transfer", label: "Bank Transfer" },
  { value: "cheque", label: "Cheque" },
  { value: "cash", label: "Cash" },
  { value: "credit_card", label: "Credit Card" },
  { value: "upi", label: "UPI/Online" },
  { value: "other", label: "Other" }
];

const ENTRY_TYPES = [
  { value: "payment", label: "Payment" },
  { value: "advance", label: "Advance" },
  { value: "credit_note", label: "Credit Note" },
  { value: "debit_note", label: "Debit Note" },
  { value: "adjustment", label: "Adjustment" }
];

const VendorDetailPage = () => {
  const { vendorId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { formatCurrency } = useSettings();
  
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('performance');
  
  // Data
  const [vendor, setVendor] = useState(null);
  const [performance, setPerformance] = useState(null);
  const [ledger, setLedger] = useState(null);
  const [pos, setPos] = useState([]);
  
  // Payment dialog
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    entry_type: 'payment',
    amount: '',
    payment_mode: 'bank_transfer',
    payment_date: new Date().toISOString().split('T')[0],
    reference_number: '',
    linked_po_ids: [],  // Changed to array for multiple POs
    notes: ''
  });
  const [submittingPayment, setSubmittingPayment] = useState(false);
  
  // Delete transaction
  const [deletingTransaction, setDeletingTransaction] = useState(null);

  useEffect(() => {
    if (vendorId) {
      loadVendorData();
    }
  }, [vendorId]);

  const loadVendorData = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Load vendor details
      const vendorRes = await axios.get(`${API_BASE_URL}/enterprise/vendors/${vendorId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVendor(vendorRes.data);
      
      // Load performance metrics
      const perfRes = await axios.get(`${API_BASE_URL}/enterprise/vendors/${vendorId}/performance`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPerformance(perfRes.data);
      
      // Load ledger
      const ledgerRes = await axios.get(`${API_BASE_URL}/enterprise/vendors/${vendorId}/ledger`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLedger(ledgerRes.data);
      
      // Load POs for payment linking
      const posRes = await axios.get(`${API_BASE_URL}/enterprise/purchase-orders?vendor_id=${vendorId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPos(posRes.data.purchase_orders || []);
      
    } catch (error) {
      toast.error('Failed to load vendor data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddPayment = async () => {
    if (!paymentForm.amount || parseFloat(paymentForm.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    
    setSubmittingPayment(true);
    try {
      const token = localStorage.getItem('token');
      
      // Support multiple PO links
      const paymentData = {
        ...paymentForm,
        amount: parseFloat(paymentForm.amount),
        linked_po_ids: paymentForm.linked_po_ids || [],
        // Keep backward compatibility - first PO as linked_po_id
        linked_po_id: paymentForm.linked_po_ids?.length > 0 ? paymentForm.linked_po_ids[0] : '',
        created_by: user?.id,
        created_by_name: user?.full_name
      };
      
      await axios.post(`${API_BASE_URL}/enterprise/vendors/${vendorId}/payments`, paymentData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Payment recorded successfully');
      setPaymentDialogOpen(false);
      setPaymentForm({
        entry_type: 'payment',
        amount: '',
        payment_mode: 'bank_transfer',
        payment_date: new Date().toISOString().split('T')[0],
        reference_number: '',
        linked_po_ids: [],
        notes: ''
      });
      
      // Reload ledger
      loadVendorData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record payment');
    } finally {
      setSubmittingPayment(false);
    }
  };

  const handleDeleteTransaction = async (entry) => {
    // Only allow deleting payment-type entries
    if (entry.type === 'po_credit') {
      toast.error('PO entries cannot be deleted. Delete the PO instead.');
      return;
    }
    
    if (!window.confirm(`Are you sure you want to delete this ${entry.type_label} of ${formatCurrency(entry.debit || entry.credit)}?`)) {
      return;
    }
    
    setDeletingTransaction(entry.id);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE_URL}/enterprise/vendors/${vendorId}/payments/${entry.payment_id || entry.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Transaction deleted successfully');
      loadVendorData(); // Reload to refresh ledger
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete transaction');
    } finally {
      setDeletingTransaction(null);
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'active': return 'bg-green-500/20 text-green-400';
      case 'on_hold': return 'bg-yellow-500/20 text-yellow-400';
      case 'blacklisted': return 'bg-red-500/20 text-red-400';
      default: return 'bg-gray-500/20 text-gray-400';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-wt-gold"></div>
      </div>
    );
  }

  if (!vendor) {
    return (
      <div className="p-6 text-center">
        <AlertTriangle className="h-12 w-12 text-red-400 mx-auto mb-4" />
        <h2 className="text-xl text-wt-text-primary">Vendor Not Found</h2>
        <Button onClick={() => navigate('/vendors')} className="mt-4">
          Back to Vendors
        </Button>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="vendor-detail-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/vendors')}
            className="text-wt-text-muted hover:text-wt-text-primary"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl md:text-3xl font-bold font-playfair text-wt-text-primary">
                {vendor.vendor_name}
              </h1>
              <Badge className={`${getStatusColor(vendor.status)} border-0`}>
                {vendor.status?.replace('_', ' ').toUpperCase()}
              </Badge>
            </div>
            <p className="text-wt-gold font-medium">{vendor.vendor_code}</p>
          </div>
        </div>
        
        <Button 
          onClick={() => setPaymentDialogOpen(true)}
          className="bg-wt-gold hover:bg-wt-gold-hover text-black"
          data-testid="add-payment-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Payment
        </Button>
      </div>

      {/* Vendor Info Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-wt-paper border border-wt-border rounded-xl p-6"
      >
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <div>
            <p className="text-sm text-wt-text-muted">Category</p>
            <p className="text-wt-text-primary font-medium">{vendor.category || '-'}</p>
          </div>
          <div>
            <p className="text-sm text-wt-text-muted">Contact Person</p>
            <p className="text-wt-text-primary font-medium">{vendor.contact_person || '-'}</p>
          </div>
          <div>
            <p className="text-sm text-wt-text-muted">Phone</p>
            <p className="text-wt-text-primary font-medium">{vendor.phone || '-'}</p>
          </div>
          <div>
            <p className="text-sm text-wt-text-muted">Payment Terms</p>
            <p className="text-wt-text-primary font-medium">{vendor.payment_terms || 'Net 30'}</p>
          </div>
        </div>
      </motion.div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-wt-paper border border-wt-border p-1">
          <TabsTrigger 
            value="performance" 
            className="data-[state=active]:bg-wt-gold data-[state=active]:text-black"
          >
            <BarChart3 className="h-4 w-4 mr-2" />
            Performance
          </TabsTrigger>
          <TabsTrigger 
            value="ledger"
            className="data-[state=active]:bg-wt-gold data-[state=active]:text-black"
          >
            <Receipt className="h-4 w-4 mr-2" />
            Ledger & Payments
          </TabsTrigger>
        </TabsList>

        {/* Performance Tab */}
        <TabsContent value="performance" className="space-y-6">
          {/* Summary KPI Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: 'Total POs', value: performance?.summary?.total_po_count || 0, icon: Package, color: 'text-wt-gold' },
              { label: 'Total Purchase', value: formatCurrency(performance?.summary?.total_purchase_value || 0), icon: DollarSign, color: 'text-green-400' },
              { label: 'Pending Value', value: formatCurrency(performance?.summary?.pending_po_value || 0), icon: Clock, color: 'text-orange-400' },
              { label: 'On-Time %', value: `${performance?.delivery_metrics?.on_time_percentage || 0}%`, icon: CheckCircle, color: 'text-blue-400' }
            ].map((stat, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                className="bg-wt-paper border border-wt-border rounded-xl p-4"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-wt-text-muted text-sm">{stat.label}</p>
                    <p className={`text-2xl font-bold ${stat.color}`}>{stat.value}</p>
                  </div>
                  <stat.icon className={`h-8 w-8 ${stat.color} opacity-50`} />
                </div>
              </motion.div>
            ))}
          </div>

          {/* Delivery & Quality Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Delivery Metrics */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-wt-paper border border-wt-border rounded-xl p-6"
            >
              <h3 className="text-lg font-semibold text-wt-text-primary mb-4 flex items-center gap-2">
                <Truck className="h-5 w-5 text-wt-gold" />
                Delivery Metrics
              </h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center p-3 bg-wt-subtle rounded-lg">
                  <span className="text-wt-text-muted">On-Time Deliveries</span>
                  <span className="text-green-400 font-bold">{performance?.delivery_metrics?.on_time_count || 0}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-wt-subtle rounded-lg">
                  <span className="text-wt-text-muted">Delayed Deliveries</span>
                  <span className="text-red-400 font-bold">{performance?.delivery_metrics?.delayed_count || 0}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-wt-subtle rounded-lg">
                  <span className="text-wt-text-muted">Delay Rate</span>
                  <span className="text-orange-400 font-bold">{performance?.delivery_metrics?.delay_percentage || 0}%</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-wt-subtle rounded-lg">
                  <span className="text-wt-text-muted">Avg Lead Time</span>
                  <span className="text-wt-text-primary font-bold">{performance?.delivery_metrics?.avg_lead_time_days || 0} days</span>
                </div>
              </div>
            </motion.div>

            {/* Quality Metrics */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-wt-paper border border-wt-border rounded-xl p-6"
            >
              <h3 className="text-lg font-semibold text-wt-text-primary mb-4 flex items-center gap-2">
                <PieChart className="h-5 w-5 text-wt-gold" />
                Quality Metrics
              </h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center p-3 bg-wt-subtle rounded-lg">
                  <span className="text-wt-text-muted">Total Units Ordered</span>
                  <span className="text-wt-text-primary font-bold">{performance?.quality_metrics?.total_units_ordered || 0}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-wt-subtle rounded-lg">
                  <span className="text-wt-text-muted">Total Units Received</span>
                  <span className="text-green-400 font-bold">{performance?.quality_metrics?.total_units_received || 0}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-wt-subtle rounded-lg">
                  <span className="text-wt-text-muted">Damaged Units</span>
                  <span className="text-red-400 font-bold">{performance?.quality_metrics?.total_units_damaged || 0}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-wt-subtle rounded-lg">
                  <span className="text-wt-text-muted">Damage Rate</span>
                  <span className={`font-bold ${(performance?.quality_metrics?.damage_percentage || 0) > 5 ? 'text-red-400' : 'text-green-400'}`}>
                    {performance?.quality_metrics?.damage_percentage || 0}%
                  </span>
                </div>
                <div className="flex justify-between items-center p-3 bg-wt-subtle rounded-lg">
                  <span className="text-wt-text-muted">Replacement Orders</span>
                  <span className="text-orange-400 font-bold">{performance?.quality_metrics?.replacement_count || 0}</span>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Monthly Trend */}
          {performance?.monthly_trend?.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-wt-paper border border-wt-border rounded-xl p-6"
            >
              <h3 className="text-lg font-semibold text-wt-text-primary mb-4 flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-wt-gold" />
                Monthly Trend
              </h3>
              <div className="grid grid-cols-6 gap-4">
                {performance.monthly_trend.map((month, i) => (
                  <div key={i} className="text-center p-3 bg-wt-subtle rounded-lg">
                    <p className="text-xs text-wt-text-muted">{month.month}</p>
                    <p className="text-wt-gold font-bold text-lg">{month.po_count}</p>
                    <p className="text-xs text-wt-text-muted">{formatCurrency(month.value)}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </TabsContent>

        {/* Ledger Tab */}
        <TabsContent value="ledger" className="space-y-6">
          {/* Financial Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
            {[
              { label: 'Total Purchase', value: formatCurrency(ledger?.summary?.total_purchase || 0), icon: Package, color: 'text-wt-gold' },
              { label: 'Payable Now', value: formatCurrency(ledger?.summary?.payable_now || 0), icon: Wallet, color: 'text-amber-400' },
              { label: 'Committed', value: formatCurrency(ledger?.summary?.committed || 0), icon: Clock, color: 'text-cyan-400' },
              { label: 'Total Paid', value: formatCurrency(ledger?.summary?.total_paid || 0), icon: CheckCircle, color: 'text-green-400' },
              { label: 'Current Due', value: formatCurrency(ledger?.summary?.current_due || 0), icon: Wallet, color: 'text-orange-400' },
              { label: 'Advance Balance', value: formatCurrency(ledger?.summary?.advance_balance || 0), icon: CreditCard, color: 'text-blue-400' },
              { label: 'Overdue', value: formatCurrency(ledger?.summary?.overdue || 0), icon: AlertTriangle, color: 'text-red-400' }
            ].map((stat, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                className="bg-wt-paper border border-wt-border rounded-xl p-4"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-wt-text-muted text-xs">{stat.label}</p>
                    <p className={`text-xl font-bold ${stat.color}`}>{stat.value}</p>
                  </div>
                  <stat.icon className={`h-6 w-6 ${stat.color} opacity-50`} />
                </div>
              </motion.div>
            ))}
          </div>

          {/* Ledger Table */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-wt-paper border border-wt-border rounded-xl overflow-hidden"
          >
            <div className="p-4 border-b border-wt-border flex items-center justify-between">
              <h3 className="text-lg font-semibold text-wt-text-primary flex items-center gap-2">
                <Receipt className="h-5 w-5 text-wt-gold" />
                Ledger Entries
              </h3>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
                    const rows = [['Date', 'Type', 'Reference', 'Description', 'Credit', 'Debit']];
                    (ledger?.ledger_entries || []).forEach(e => rows.push([e.date, e.type_label, e.reference || '', e.description || '', e.credit || 0, e.debit || 0]));
                    const s = ledger?.summary || {};
                    rows.push([], ['Total Purchase', s.total_purchase], ['Payable Now', s.payable_now], ['Committed (not received)', s.committed], ['Total Paid', s.total_paid], ['Current Due', s.current_due], ['Advance Balance', s.advance_balance], ['Overdue', s.overdue]);
                    const csv = rows.map(r => r.map(esc).join(',')).join('\n');
                    const a = document.createElement('a');
                    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
                    a.download = `vendor-statement-${ledger?.vendor?.vendor_code || vendorId}.csv`;
                    a.click();
                  }}
                  className="border-wt-border text-wt-text-secondary hover:text-wt-text-primary h-8 text-xs"
                  data-testid="export-statement-csv-btn"
                >
                  <Download className="h-3.5 w-3.5 mr-1" />
                  Export Statement
                </Button>
                <Badge className="bg-wt-subtle text-wt-text-primary border-0">
                  {ledger?.ledger_entries?.length || 0} entries
                </Badge>
              </div>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-wt-subtle">
                  <tr>
                    <th className="text-left p-4 text-wt-text-muted text-sm">Date</th>
                    <th className="text-left p-4 text-wt-text-muted text-sm">Type</th>
                    <th className="text-left p-4 text-wt-text-muted text-sm">Reference</th>
                    <th className="text-left p-4 text-wt-text-muted text-sm">Description</th>
                    <th className="text-right p-4 text-wt-text-muted text-sm">Credit</th>
                    <th className="text-right p-4 text-wt-text-muted text-sm">Debit</th>
                    <th className="text-right p-4 text-wt-text-muted text-sm">Balance</th>
                    <th className="text-center p-4 text-wt-text-muted text-sm">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    // Compute the running balance per entry by walking
                    // the ledger chronologically (oldest → newest). The
                    // table is displayed newest-first, so we map the
                    // computed balances back onto the original order.
                    // Balance convention: credit (PO created) increases
                    // our payable; debit (payment we made) decreases it.
                    const entries = ledger?.ledger_entries || [];
                    const ordered = [...entries].sort((a, b) => {
                      const da = new Date(a.date).getTime() || 0;
                      const db = new Date(b.date).getTime() || 0;
                      if (da !== db) return da - db;
                      // Stable tiebreaker so balance is deterministic
                      return (a.id || '').localeCompare(b.id || '');
                    });
                    let running = 0;
                    const balanceById = {};
                    ordered.forEach((e) => {
                      running += Number(e.credit || 0) - Number(e.debit || 0);
                      balanceById[e.id] = running;
                    });
                    return entries.map((entry) => (
                    <tr key={entry.id} className="border-t border-wt-border/50 hover:bg-wt-subtle/30">
                      <td className="p-4 text-wt-text-primary">{entry.date}</td>
                      <td className="p-4">
                        <Badge className={`border-0 ${
                          entry.type === 'po_credit' ? 'bg-blue-500/20 text-blue-400' :
                          entry.type === 'payment' ? 'bg-green-500/20 text-green-400' :
                          entry.type === 'advance' ? 'bg-purple-500/20 text-purple-400' :
                          'bg-gray-500/20 text-gray-400'
                        }`}>
                          {entry.type_label}
                        </Badge>
                      </td>
                      <td className="p-4 text-wt-gold font-medium">{entry.reference || '-'}</td>
                      <td className="p-4 text-wt-text-primary text-sm max-w-xs">
                        <div className="truncate">{entry.description}</div>
                        {entry.linked_po_number && (
                          <div className="text-xs text-wt-text-muted flex items-center gap-1 mt-1">
                            <span className="text-wt-gold">{entry.linked_po_number}</span>
                          </div>
                        )}
                      </td>
                      <td className="p-4 text-right">
                        {entry.credit > 0 && (
                          <span className="text-blue-400 font-semibold">{formatCurrency(entry.credit)}</span>
                        )}
                      </td>
                      <td className="p-4 text-right">
                        {entry.debit > 0 && (
                          <span className="text-green-400 font-semibold">{formatCurrency(entry.debit)}</span>
                        )}
                      </td>
                      <td className="p-4 text-right">
                        {(() => {
                          const bal = balanceById[entry.id] || 0;
                          // Positive = we owe vendor (payable). Negative
                          // = vendor owes us (advance). Color accordingly.
                          const tone =
                            bal > 0 ? 'text-orange-400'
                            : bal < 0 ? 'text-purple-400'
                            : 'text-wt-text-muted';
                          return (
                            <span
                              className={`font-semibold ${tone}`}
                              data-testid={`vendor-ledger-balance-${entry.id}`}
                            >
                              {formatCurrency(Math.abs(bal))}
                              {bal < 0 && <span className="ml-1 text-[10px] uppercase">Cr</span>}
                            </span>
                          );
                        })()}
                      </td>
                      <td className="p-4 text-center">
                        {entry.type !== 'po_credit' ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteTransaction(entry)}
                            disabled={deletingTransaction === entry.id}
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                            title="Delete this transaction"
                          >
                            {deletingTransaction === entry.id ? (
                              <div className="h-4 w-4 animate-spin rounded-full border-2 border-red-400 border-t-transparent" />
                            ) : (
                              <Trash2 className="h-4 w-4" />
                            )}
                          </Button>
                        ) : (
                          <span className="text-wt-text-muted text-xs">-</span>
                        )}
                      </td>
                    </tr>
                    ));
                  })()}
                  
                  {(!ledger?.ledger_entries || ledger.ledger_entries.length === 0) && (
                    <tr>
                      <td colSpan={8} className="p-8 text-center text-wt-text-muted">
                        No ledger entries found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </motion.div>
        </TabsContent>
      </Tabs>

      {/* Add Payment Dialog */}
      <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
        <DialogContent className="bg-wt-paper border-wt-border max-w-md">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-wt-gold" />
              Record Payment
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Entry Type */}
            <div>
              <Label className="text-wt-text-primary">Entry Type</Label>
              <Select 
                value={paymentForm.entry_type} 
                onValueChange={(v) => setPaymentForm({...paymentForm, entry_type: v})}
              >
                <SelectTrigger className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-wt-paper border-wt-border">
                  {ENTRY_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Amount */}
            <div>
              <Label className="text-wt-text-primary">Amount *</Label>
              <Input
                type="number"
                value={paymentForm.amount}
                onChange={(e) => setPaymentForm({...paymentForm, amount: e.target.value})}
                className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary"
                placeholder="0.00"
                data-testid="payment-amount"
              />
            </div>
            
            {/* Payment Mode */}
            <div>
              <Label className="text-wt-text-primary">Payment Mode</Label>
              <Select 
                value={paymentForm.payment_mode} 
                onValueChange={(v) => setPaymentForm({...paymentForm, payment_mode: v})}
              >
                <SelectTrigger className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-wt-paper border-wt-border">
                  {PAYMENT_MODES.map(m => (
                    <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Date and Reference */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-wt-text-primary">Payment Date</Label>
                <DateInput
                  value={paymentForm.payment_date}
                  onChange={(e) => setPaymentForm({...paymentForm, payment_date: e.target.value})}
                  className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary"
                />
              </div>
              <div>
                <Label className="text-wt-text-primary">Reference #</Label>
                <Input
                  value={paymentForm.reference_number}
                  onChange={(e) => setPaymentForm({...paymentForm, reference_number: e.target.value})}
                  className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary"
                  placeholder="TXN/CHQ #"
                />
              </div>
            </div>
            
            {/* Link to POs - Multi-select */}
            <div>
              <Label className="text-wt-text-primary">Link to POs (Optional)</Label>
              <div className="mt-2 border border-wt-border rounded-md max-h-[180px] overflow-y-auto bg-wt-bg">
                {pos
                  .filter(p => p.status !== 'cancelled' && p.status !== 'draft')
                  .filter(p => {
                    const paidAmount = p.paid_amount || 0;
                    const totalAmount = p.total_amount || 0;
                    return paidAmount < totalAmount;
                  })
                  .length === 0 ? (
                    <div className="p-3 text-center text-wt-text-muted text-sm">
                      No unpaid POs available
                    </div>
                  ) : (
                    pos
                      .filter(p => p.status !== 'cancelled' && p.status !== 'draft')
                      .filter(p => {
                        const paidAmount = p.paid_amount || 0;
                        const totalAmount = p.total_amount || 0;
                        return paidAmount < totalAmount;
                      })
                      .map(po => {
                        const remaining = (po.total_amount || 0) - (po.paid_amount || 0);
                        const isSelected = paymentForm.linked_po_ids?.includes(po.id);
                        return (
                          <div 
                            key={po.id}
                            className={`flex items-center gap-3 p-3 border-b border-wt-border last:border-0 cursor-pointer hover:bg-wt-paper transition-colors ${isSelected ? 'bg-wt-gold/10' : ''}`}
                            onClick={() => {
                              const currentIds = paymentForm.linked_po_ids || [];
                              const newIds = isSelected
                                ? currentIds.filter(id => id !== po.id)
                                : [...currentIds, po.id];
                              setPaymentForm({...paymentForm, linked_po_ids: newIds});
                            }}
                          >
                            <Checkbox 
                              checked={isSelected}
                              className="border-wt-border data-[state=checked]:bg-wt-gold data-[state=checked]:border-wt-gold"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-wt-text-primary">{po.po_number}</span>
                                <Badge className="bg-wt-gold/20 text-wt-gold text-xs">
                                  {formatCurrency(po.total_amount)}
                                </Badge>
                              </div>
                              <p className="text-xs text-wt-text-muted mt-0.5">
                                Due: <span className="text-red-400">{formatCurrency(remaining)}</span>
                              </p>
                            </div>
                          </div>
                        );
                      })
                  )}
              </div>
              {paymentForm.linked_po_ids?.length > 0 && (
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                  <span className="text-xs text-wt-text-muted">Selected:</span>
                  {paymentForm.linked_po_ids.map(poId => {
                    const po = pos.find(p => p.id === poId);
                    return po ? (
                      <Badge key={poId} className="bg-wt-gold/20 text-wt-gold text-xs">
                        {po.po_number}
                        <XCircle 
                          className="h-3 w-3 ml-1 cursor-pointer hover:text-red-400" 
                          onClick={(e) => {
                            e.stopPropagation();
                            setPaymentForm({
                              ...paymentForm, 
                              linked_po_ids: paymentForm.linked_po_ids.filter(id => id !== poId)
                            });
                          }}
                        />
                      </Badge>
                    ) : null;
                  })}
                </div>
              )}
            </div>
            
            {/* Notes */}
            <div>
              <Label className="text-wt-text-primary">Notes</Label>
              <Textarea
                value={paymentForm.notes}
                onChange={(e) => setPaymentForm({...paymentForm, notes: e.target.value})}
                className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary"
                placeholder="Payment notes..."
                rows={2}
              />
            </div>
            
            {/* Actions */}
            <div className="flex justify-end gap-3 pt-2">
              <Button 
                variant="ghost" 
                onClick={() => setPaymentDialogOpen(false)} 
                className="text-wt-text-secondary"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleAddPayment}
                disabled={submittingPayment}
                className="bg-wt-gold hover:bg-wt-gold-hover text-black"
                data-testid="confirm-payment"
              >
                {submittingPayment ? 'Recording...' : 'Record Payment'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VendorDetailPage;
